// @ts-nocheck
import { a, getDisplayedShapes, getNetElements } from "../../shared/globalState/a";
import { ElbowBlock } from "../resistances/ElbowBlock";
import { Tee } from "../resistances/Tee";
import { Block } from "../shapes/Block";
import { Line } from "../shapes/Line";
import { getVelocityRound, getVelocityRect, getDpByKms } from "../../services/calculations";
import { ObjectTypes } from "../../shared/enums";
import { FlowDirection } from "../../shared/types";
import { extractNodeIdsWithFittings } from "../../services/network/NetworkHelpers";
import { isin } from "../../shared/common";
import { getNetworkElementsByNodes, getRelatedNodeFromFittingNode, getBranchByElement as getBranchByElementFn } from "../../services/graph&network";
import { TeeExhaustRectangular, TeeExhaustRound, TeeSupplyRectangular, TeeSupplyRound } from "../../libs/hydraulic-resistance";
import { getashapes } from "../../shared/globalState/a";

export class Branch {
    id: string;
    path: string[];
    elements: (Line | Block | ElbowBlock | Tee)[];
    endFlowRate: number;
    dp: number;

    // Performance optimizations: cached data structures
    private _pathIndexMap: Map<string, number> | null = null;
    private _cachedGraph: any = null;
    private _pathVersion: number = 0;

    constructor() {
        this.id = '';
        this.path = [];
        this.elements = [];
        this.endFlowRate = 0;
        this.dp = 0;
    }

    /**
     * Get cached graph for this branch
     * Caches the graph lookup to avoid repeated traversals
     */
    getGraph(): any {
        if (!this._cachedGraph && this.elements.length > 0) {
            this._cachedGraph = this.elements[0].getGraph();
        }
        return this._cachedGraph;
    }

    /**
     * Invalidate cached graph (call when branch structure changes)
     */
    invalidateGraphCache(): void {
        this._cachedGraph = null;
    }

    /**
     * Get index of node in branch path (O(1) lookup)
     * @param nodeId - Node ID to find
     * @returns Index in path, or -1 if not found
     */
    getPathIndex(nodeId: string): number {
        this._ensurePathIndexMap();
        return this._pathIndexMap!.get(nodeId) ?? -1;
    }

    /**
     * Check if node is in branch path (O(1) lookup)
     * @param nodeId - Node ID to check
     * @returns true if node is in path
     */
    isInPath(nodeId: string): boolean {
        return this.getPathIndex(nodeId) >= 0;
    }

    /**
     * Ensure path index map is built and up-to-date
     */
    private _ensurePathIndexMap(): void {
        const currentVersion = this.path.length;
        if (!this._pathIndexMap || this._pathVersion !== currentVersion) {
            this._pathIndexMap = new Map(
                this.path.map((id, index) => [id, index])
            );
            this._pathVersion = currentVersion;
        }
    }

    /**
     * Check if this branch is affected by the given element
     * @param elementId - ID of the element to check
     * @returns true if branch contains or is connected to the element
     */
    isAffectedBy(elementId: string): boolean {
        // Check if element is directly in this branch
        if (this.elements.some(e => e.id === elementId)) {
            return true;
        }
        
        // Check if element is connected to any element in this branch
        // (This is a simplified check - can be enhanced based on network topology)
        const element = this.elements.find(e => e.id === elementId);
        if (element) {
            return true;
        }
        
        return false;
    }

    /**
     * Check if a Block element is a diffuser based on its code
     * @param block - Block element to check
     * @returns true if the block is a diffuser (5 characters, starts with "105")
     */
    private isDiffuser(block: Block): boolean {
        if (!block.data || !block.data.code) {
            return false;
        }

        const code = block.data.code.toString();
        return code.length === 5 && code.startsWith('105');
    }

    /**
     * Calculate and set the end flow rate based on diffuser elements in this branch
     * @param fallbackFlowRate - Flow rate to use if no diffusers found
     */
    calculateEndFlowRate(fallbackFlowRate: number = 0): void {
        const diffuserFlowRates: number[] = [];

        this.elements.forEach(element => {
            if (element instanceof Block && element.checkIsTerminal()) {
                if (element.net && element.net.flowRateCmh) {
                    diffuserFlowRates.push(element.net.flowRateCmh);
                }
            }
        });

        if (diffuserFlowRates.length > 0) {
            // Sum all diffuser flow rates in this branch
            this.endFlowRate = diffuserFlowRates.reduce((sum, rate) => sum + rate, 0);
        } else {
            // Fallback: use provided fallback flow rate
            this.endFlowRate = fallbackFlowRate;
        }
    }

    assignCumulativeFlowRateToElements(): void {
        const branchFlowRates = new Map(a.branches.map((branch) => [branch.id, branch.endFlowRate]));

        this.elements.forEach((elem) => {
            const cumulativeFlowRate = elem.net.getCumulativeFlowRate(branchFlowRates);
            elem.net.flowRateCmh = cumulativeFlowRate;
        });
    }

    /**
     * Static method to assign flow rates to all elements across all branches
     * This ensures shared elements get cumulative flow rates correctly
     * Should be called after all branches have calculated their endFlowRate
     */
    /**
     * Assigns flow rates to all network elements based on their branch membership.
     * 
     * Logic:
     * - Elements belonging to only one branch: use that branch's endFlowRate
     * - Elements shared across multiple branches: use cumulative flow rate (sum of all branch flows)
     * 
     * This ensures correct flow rates for both dedicated segments (connected to terminals)
     * and shared upstream segments (common parts of the network).
     */
    static assignCumulativeFlowRates(): void {
        // Early return if no branches exist
        if (!a.branches || a.branches.length === 0) {
            return;
        }

        // Get all network elements (Lines, Blocks, ElbowBlocks, Tees)
        // These are the elements that can have flow rates assigned
        const elements = getNetElements() as (Line | ElbowBlock | Tee | Block)[];
        
        // Create a map of branch flow rates for O(1) lookup
        // Format: Map<branchId, endFlowRate>
        // Example: { "branch-0": 500, "branch-1": 500, "branch-2": 500 }
        const branchFlowRates = new Map(a.branches.map(b => [b.id, b.endFlowRate]));

        // Categorize elements by branch membership:
        // - Single-branch elements: belong to exactly one branch (e.g., segments connected to terminals)
        //   These should use that branch's flow rate only
        // - Shared elements: belong to multiple branches (e.g., common upstream segments)
        //   These should use cumulative flow rate (sum of all branch flows)
        const singleBranchElements = elements.filter(el => 
            el.net && el.net.branchIds && el.net.branchIds.length === 1
        );
        const sharedElements = elements.filter(el => 
            el.net && el.net.branchIds && el.net.branchIds.length > 1
        );

        // Assign flow rates to single-branch elements
        // These elements belong to only one branch, so they get that branch's endFlowRate
        // Example: element in branch-0 with endFlowRate=500 → flowRateCmh=500
        singleBranchElements.forEach(el => {
            if (el.net && el.net.branchIds.length === 1) {
                const branchId = el.net.branchIds[0]; // Get the single branch ID
                el.net.flowRateCmh = branchFlowRates.get(branchId) || 0; // Use that branch's flow rate
            }
        });

        // Assign cumulative flow rates to shared elements
        // These elements belong to multiple branches, so they get the sum of all branch flows
        // Example: element in [branch-0, branch-1, branch-2] with each branch=500 → flowRateCmh=1500
        // The getCumulativeFlowRate() method sums up flow rates from all branches in branchIds
        sharedElements.forEach(el => {
            if (el.net) {
                // getCumulativeFlowRate() iterates through branchIds and sums their flow rates
                el.net.flowRateCmh = el.net.getCumulativeFlowRate(branchFlowRates);
            }
        });
    }

    /**
     * Static method to get a branch that contains the given element
     * @param element - The element to find the branch for
     * @returns The branch containing the element, or null if not found
     */
    static getBranchByElement(element: Line | Block | ElbowBlock | Tee): Branch | null {
        return getBranchByElementFn(element);
    }

    /**
     * Execute the complete calculation sequence for this branch
     * This includes assigning sizes, flow rates, velocities, and calculating delta pressures
     * Performance optimization: Combines tee operations to avoid redundant segment lookups
     * @param includeEndFlowRate - Whether to calculate end flow rate first (default: false)
     */
    executeCalculations(includeEndFlowRate: boolean = false): void {
        
        // Performance optimization: Assign tee sizes and flows together to avoid redundant lookups
        // Both operations need the same segment lookups, so we combine them
        this.assignTeeSizeAndFlows();
        
        // Calculate tee velocities based on sizes and flows
        this.assignTeeVelocities();
        
        // Calculate tee dp using assigned flows, sizes, and velocities with hydraulic resistance library
        this.calculateTeeDp();
        
        // Finally, calculate branch dp (sums all element dp including tees)
        this.calculateDp();
    }

    /**
     * Combined tee size and flow assignment (performance optimization)
     * This avoids redundant segment lookups by doing both operations together
     */
    private assignTeeSizeAndFlows(): void {
        const graf = this.getGraph();
        if (!graf) return;
        
        const tees = this.elements.filter(e => e instanceof Tee) as Tee[];
        
        tees.forEach(tee => {
            const crucialPoints = tee.getCrucialPoints();
            const start = crucialPoints.start;
            const end = crucialPoints.end;
            const mid = crucialPoints.mid;

            const relatedSegmentPointOnStart = graf.getLineNodeIdByFittingNodeId(start.id);
            const relatedSegmentPointOnEnd = graf.getLineNodeIdByFittingNodeId(end.id);
            const relatedSegmentPointOnMid = graf.getLineNodeIdByFittingNodeId(mid.id);

            if (!relatedSegmentPointOnEnd && !relatedSegmentPointOnStart && !relatedSegmentPointOnMid) {
                return;
            }

            // Lookup segments once (shared by both size and flow assignment)
            const segmentsOnStart = getNetworkElementsByNodes([relatedSegmentPointOnStart]);
            const segmentsOnEnd = getNetworkElementsByNodes([relatedSegmentPointOnEnd]);
            const segmentsOnMid = getNetworkElementsByNodes([relatedSegmentPointOnMid]);

            const segmentOnStart = segmentsOnStart?.[0] as Line | undefined;
            const segmentOnEnd = segmentsOnEnd?.[0] as Line | undefined;
            const segmentOnMid = segmentsOnMid?.[0] as Line | undefined;

            // Determine which nodes are in the branch path (using O(1) lookup)
            const startInPath = relatedSegmentPointOnStart ? this.isInPath(relatedSegmentPointOnStart) : false;
            const endInPath = relatedSegmentPointOnEnd ? this.isInPath(relatedSegmentPointOnEnd) : false;
            const midInPath = relatedSegmentPointOnMid ? this.isInPath(relatedSegmentPointOnMid) : false;
            
            // Helper function to assign size from segment
            const assignSizeFromSegment = (segment: Line | undefined, isRound: boolean): { width: number; height: number } | null => {
                if (!segment || !segment.net) {
                    return null;
                }
                
                if (isRound) {
                    const diameter = segment.net.diameterMil || segment.net.widthMil;
                    return { width: diameter, height: diameter };
                } else {
                    return { width: segment.net.widthMil, height: segment.net.heightMil };
                }
            };

            // Determine collector and pass segments based on order in branch path
            let collectorSegment: Line | undefined;
            let passSegment: Line | undefined;
            
            if (startInPath && endInPath) {
                // Both start and end are in path - determine order (using O(1) lookup)
                const startIndex = this.getPathIndex(relatedSegmentPointOnStart!);
                const endIndex = this.getPathIndex(relatedSegmentPointOnEnd!);
                
                if (startIndex < endIndex) {
                    // start is upstream (collector), end is downstream (pass)
                    collectorSegment = segmentOnStart;
                    passSegment = segmentOnEnd;
                } else {
                    // end is upstream (collector), start is downstream (pass)
                    collectorSegment = segmentOnEnd;
                    passSegment = segmentOnStart;
                }
            } else if (startInPath) {
                // Only start is in path - it's the collector
                collectorSegment = segmentOnStart;
                passSegment = segmentOnEnd; // pass might be on end if not in path
            } else if (endInPath) {
                // Only end is in path - it's the collector
                collectorSegment = segmentOnEnd;
                passSegment = segmentOnStart; // pass might be on start if not in path
            } else {
                // Neither in path - use start as collector, end as pass
                collectorSegment = segmentOnStart;
                passSegment = segmentOnEnd;
            }

            // Assign collector sizes
            if (collectorSegment) {
                const isRound = collectorSegment.segmentShape === ObjectTypes.round;
                const collectorSize = assignSizeFromSegment(collectorSegment, isRound);
                
                if (collectorSize) {
                    tee.widthCollectorMillimeter = collectorSize.width;
                    tee.heightCollectorMillimeter = collectorSize.height;
                    tee.shape = isRound ? ObjectTypes.round : ObjectTypes.rect;
                }
            }

            // Assign pass sizes
            if (passSegment) {
                const isRound = passSegment.segmentShape === ObjectTypes.round;
                const passSize = assignSizeFromSegment(passSegment, isRound);
                
                if (passSize) {
                    tee.widthPassMillimeter = passSize.width;
                    tee.heightPassMillimeter = passSize.height;
                    // Ensure shape is set if not already set from collector
                    if (!tee.shape) {
                        tee.shape = isRound ? ObjectTypes.round : ObjectTypes.rect;
                    }
                }
            }

            // Assign turn sizes (always from mid/side)
            if (segmentOnMid) {
                const isRound = segmentOnMid.segmentShape === ObjectTypes.round;
                const turnSize = assignSizeFromSegment(segmentOnMid, isRound);
                
                if (turnSize) {
                    tee.widthTurnMillimeter = turnSize.width;
                    tee.heightTurnMillimeter = turnSize.height;
                }
            }

            // Now assign flows (using the same segments we already looked up)
            const flowOnStart = segmentOnStart?.net?.flowRateCmh || 0;
            const flowOnEnd = segmentOnEnd?.net?.flowRateCmh || 0;
            const flowOnMid = segmentOnMid?.net?.flowRateCmh || 0;

            // Get flow direction type for this branch
            const flowType = tee.flowDirectionType.get(this.id) || 'UNKNOWN';
            
            // Determine collector and pass based on order in branch path
            // Collector is upstream (earlier in path), Pass is downstream (later in path)
            let collectorFlow = 0;
            let turnFlow = flowOnMid; // Turn flow is always from mid (side)
            
            if (startInPath && endInPath) {
                // Both start and end are in path - determine order (using O(1) lookup)
                const startIndex = this.getPathIndex(relatedSegmentPointOnStart!);
                const endIndex = this.getPathIndex(relatedSegmentPointOnEnd!);
                
                if (startIndex < endIndex) {
                    // start is upstream (collector), end is downstream (pass)
                    collectorFlow = flowOnStart;
                } else {
                    // end is upstream (collector), start is downstream (pass)
                    collectorFlow = flowOnEnd;
                }
            } else if (startInPath) {
                // Only start is in path - it's the collector
                collectorFlow = flowOnStart;
            } else if (endInPath) {
                // Only end is in path - it's the collector
                collectorFlow = flowOnEnd;
            } else {
                // Neither in path - use larger flow as collector
                collectorFlow = Math.max(flowOnStart, flowOnEnd);
            }
            
            // Assign flows to tee
            tee.flowRateCollectorCmh = collectorFlow;
            tee.flowRateTurnCmh = turnFlow;
        });
    }

    calculateDp(): void {
        // Update calculations for Line elements
        this.elements.filter((elem) => elem instanceof Line).forEach((elem) => {
            elem.net._updateCalculations();
        });
        
        // Update calculations for Block and ElbowBlock elements (but not Tees)
        // Tees are handled separately via calculateTeeDp() which should be called before this
        this.elements.filter((elem) => 
            (elem instanceof Block || elem instanceof ElbowBlock) && !(elem instanceof Tee)
        ).forEach((elem) => {
            this.assignBlockVelocitiesByConnectedSegments(elem as Block | ElbowBlock);
        });

        // Sum up dp values from all elements (including tees which should already have dp calculated)
        // Filter out NaN, undefined, and null values
        // For tees, use branch-specific DP from tee.branchDp.get(this.id) instead of tee.net.dp
        // because tee.net.dp gets overwritten by other branches
        this.dp = this.elements.reduce((sum, elem) => {
            let dpValue: number | undefined;
            
            // For tees, use branch-specific DP
            if (elem instanceof Tee) {
                dpValue = elem.branchDp?.get(this.id);
            } else {
                // For other elements, use net.dp
                dpValue = elem.net?.dp;
            }
            
            // Only add valid numbers (not NaN, undefined, or null)
            if (typeof dpValue === 'number' && !isNaN(dpValue) && isFinite(dpValue)) {
                return sum + dpValue;
            }
            return sum;
        }, 0);
    }

    /**
     * Assign velocities to blocks/elbow blocks based on connected segments
     * @param block - Block or ElbowBlock to assign velocity to
     */
    assignBlockVelocitiesByConnectedSegments(block: Block | ElbowBlock): void {
        if (!((block as any) instanceof Block) && !((block as any) instanceof ElbowBlock)) return;
        let connectedSegments = block.getConnectedSegments();

        let velocities = connectedSegments.map((s: Line) => {
            if (s.segmentShape === ObjectTypes.round) {
                let vel = getVelocityRound(s.net.diameterMil, s.net.flowRateCmh);
                return vel;
            } else if (s.segmentShape === ObjectTypes.rect) {
                let vel = getVelocityRect(s.net.widthMil, s.net.heightMil, s.net.flowRateCmh);
                return vel;
            }
            return undefined;
        });

        if (velocities.every((v) => v === velocities[0])) {
            block.net.velocity = velocities[0];
            block.net._updateCalculations();
        }
    }

    /**
     * Static method to assign velocities to blocks/elbow blocks based on connected segments
     * @param block - Block or ElbowBlock to assign velocity to
     */
    static assignBlockVelocitiesByConnectedSegments(block: Block | ElbowBlock): void {
        if (!((block as any) instanceof Block) && !((block as any) instanceof ElbowBlock)) return;
        let connectedSegments = block.getConnectedSegments();

        let velocities = connectedSegments.map((s: Line) => {
            if (s.segmentShape === ObjectTypes.round) {
                let vel = getVelocityRound(s.net.diameterMil, s.net.flowRateCmh);
                return vel;
            } else if (s.segmentShape === ObjectTypes.rect) {
                let vel = getVelocityRect(s.net.widthMil, s.net.heightMil, s.net.flowRateCmh);
                return vel;
            }
            return undefined;
        });

        if (velocities.every((v) => v === velocities[0])) {
            block.net.velocity = velocities[0];
            block.net._updateCalculations();
        }
    }



    /**
     * Get the path of node IDs in the graph for this branch
     * @returns Array of node IDs in order
     */
    getGraphPath(): string[] {
        // Find the graph that contains this branch's nodes
        const graph = a.graphs.find(g => {
            // Check if any element's crucial points exist in this graph
            return this.elements.some(elem => {
                const cp = elem.getCrucialPoints();
                const pointIds = [cp.start?.id, cp.end?.id, cp.mid?.id].filter(Boolean);
                return pointIds.some(id => g.hasNode(id));
            });
        });

        if (!graph) {
            
            return [];
        }

        // Collect all node IDs from branch elements
        const nodeIds = new Set<string>();
        
        this.elements.forEach(elem => {
            const cp = elem.getCrucialPoints();
            if (cp.start?.id) nodeIds.add(cp.start.id);
            if (cp.end?.id) nodeIds.add(cp.end.id);
            if (cp.mid?.id) nodeIds.add(cp.mid.id);
        });

        // Find terminal paths using getNetworkLoops functionality
        // Get terminal node first
        const terminalNode = graph.nodes().find(nodeId => {
            const attrs = graph.getNodeAttributes(nodeId);
            return attrs.isTerminal === true && nodeIds.has(nodeId);
        });

        if (!terminalNode) {
            // No terminal node, return all node IDs in arbitrary order
            return Array.from(nodeIds);
        }

        // Use graph findPath method to find paths from terminal node
        const pathsFromTerminal: Record<string, string[]> = {};
        
        graph.nodes().forEach(nodeId => {
            if (nodeId !== terminalNode && nodeIds.has(nodeId)) {
                try {
                    const path = graph.findPath(terminalNode, nodeId);
                    if (path && Array.isArray(path)) {
                        pathsFromTerminal[nodeId] = path as string[];
                    }
                } catch (e) {
                    // No path found
                }
            }
        });

        // Get the path to the furthest node (longest path)
        const longestPath = Object.values(pathsFromTerminal).reduce((longest, path) => {
            return (path as string[]).length > longest.length ? path as string[] : longest;
        }, [] as string[]);

        // If findPath found paths, use them
        if (longestPath.length > 0) {
            // Check if path already includes terminal node
            if (longestPath[0] === terminalNode) {
                return longestPath;
            } else {
                return [terminalNode, ...longestPath];
            }
        }
        
        // If no paths found from findPath, build path manually from terminal to other nodes
        // This is a fallback when findPath doesn't work
        const visited = new Set<string>();
        const path: string[] = [terminalNode];
        
        const buildPath = (currentNode: string): void => {
            const neighbors = graph.neighbors(currentNode);
            if (!neighbors || neighbors.length === 0) return;
            
            for (const neighbor of neighbors) {
                if (nodeIds.has(neighbor) && !visited.has(neighbor)) {
                    visited.add(neighbor);
                    path.push(neighbor);
                    buildPath(neighbor);
                    break; // Only follow first path
                }
            }
        };
        
        visited.add(terminalNode);
        buildPath(terminalNode);
        
        return path;
    }



    /**
     * Assigns flow direction type ('on pass' or 'on turn') to tees in this branch
     * based on flow directions from graph nodes in the branch path
     */
    assignTeeFlowDirectionType() {
        // Use cached graph lookup
        const graph = this.getGraph();
        if (!graph) {
            const tees = this.elements.filter(el => el instanceof Tee) as Tee[];
            console.warn(`[Tee Flow Directions] Branch ${this.id}: No graph available, setting all tees to UNKNOWN`);
            tees.forEach(tee => tee.flowDirectionType.set(this.id, 'UNKNOWN'));
            return;
        }

        const tees = this.elements.filter(el => el instanceof Tee) as Tee[];
        
        for (const tee of tees) {
            const crucialPoints = tee.getCrucialPoints();
            if (!crucialPoints.start || !crucialPoints.end || !crucialPoints.mid) {
                console.warn(`[Tee Flow Directions] Branch ${this.id}, Tee ${tee.id}: Missing crucial points`, {
                    start: !!crucialPoints.start,
                    end: !!crucialPoints.end,
                    mid: !!crucialPoints.mid
                });
                tee.flowDirectionType.set(this.id, 'UNKNOWN');
                continue;
            }

            // Convert fitting node IDs to graph node IDs
            const base1NodeId = graph.getLineNodeIdByFittingNodeId(crucialPoints.start.id);
            const base2NodeId = graph.getLineNodeIdByFittingNodeId(crucialPoints.end.id);
            const sideNodeId = graph.getLineNodeIdByFittingNodeId(crucialPoints.mid.id);

            // Check which nodes are in the branch path (using O(1) lookup)
            const base1InPath = base1NodeId ? this.isInPath(base1NodeId) : false;
            const base2InPath = base2NodeId ? this.isInPath(base2NodeId) : false;
            const sideInPath = sideNodeId ? this.isInPath(sideNodeId) : false;

            // Determine flow type: if side is in path → "on turn", else if both bases → "on pass"
            let flowType: 'on pass' | 'on turn' | 'UNKNOWN' = 'UNKNOWN';
            
            if (sideInPath) {
                // Path goes through side → "on turn"
                flowType = 'on turn';
            } else if (base1InPath && base2InPath) {
                // Path goes through both bases but not side → "on pass"
                flowType = 'on pass';
            } else {
                // Log why flowType remains UNKNOWN
                console.warn(`[Tee Flow Directions] Branch ${this.id}, Tee ${tee.id}: FlowType is UNKNOWN because:`, {
                    sideInPath,
                    base1InPath,
                    base2InPath,
                    condition: `sideInPath=${sideInPath}, base1InPath=${base1InPath}, base2InPath=${base2InPath}`,
                    reason: sideInPath ? 'N/A' : (base1InPath && base2InPath ? 'N/A' : 'Neither side in path nor both bases in path')
                });
            }

            // Only log if flowType is UNKNOWN (to debug issues)
            if (flowType === 'UNKNOWN') {
                console.warn(`[Tee Flow Directions] Branch ${this.id}, Tee ${tee.id}: FlowType is UNKNOWN`, {
                    base1NodeId,
                    base2NodeId,
                    sideNodeId,
                    base1InPath,
                    base2InPath,
                    sideInPath,
                    branchPath: this.path,
                    crucialPoints: {
                        start: crucialPoints.start.id,
                        end: crucialPoints.end.id,
                        mid: crucialPoints.mid.id
                    }
                });
            }

            tee.flowDirectionType.set(this.id, flowType);
        }
    }

    /**
     * Assign exhaust or supply type to all tees in this branch based on terminal flow direction
     * If terminal flow direction is IN, this is a supply network
     * If terminal flow direction is OUT, this is an exhaust network
     */
    assignTeeTypeExhaustOrSupply(): void {
        const terminal = this.elements.find(el => (el instanceof Block) && el.checkIsTerminal()) as Block;
        if (terminal) {
            const flowDirection = terminal.getTerminalFlowDirection();
            
            if (flowDirection === FlowDirection.IN) {
                // IN flow direction means supply network
                const tees = this.elements.filter((elem) => elem instanceof Tee) as Tee[];
                tees.forEach((tee) => {
                    tee.exhaustOrSupply = ObjectTypes.supply;
                });
            } else if (flowDirection === FlowDirection.OUT) {
                // OUT flow direction means exhaust network
                const tees = this.elements.filter((elem) => elem instanceof Tee) as Tee[];
                tees.forEach((tee) => {
                    tee.exhaustOrSupply = ObjectTypes.exhaust;
                });
            }
        }
    }

    /**
     * Assign sizes to tees from connected segments
     * Uses graph-based approach similar to assignTeeFlowsOnCrucialPoints
     * Determines collector and pass based on order in branch path
     */
    assignTeeSize(): void {
        const graf = this.getGraph();
        if (!graf) return;
        
        (this.elements.filter(e => e instanceof Tee) as Tee[]).forEach(tee => {
            const crucialPoints = tee.getCrucialPoints();
            const start = crucialPoints.start;
            const end = crucialPoints.end;
            const mid = crucialPoints.mid;

            const relatedSegmentPointOnStart = graf.getLineNodeIdByFittingNodeId(start.id);
            const relatedSegmentPointOnEnd = graf.getLineNodeIdByFittingNodeId(end.id);
            const relatedSegmentPointOnMid = graf.getLineNodeIdByFittingNodeId(mid.id);

            if (!relatedSegmentPointOnEnd && !relatedSegmentPointOnStart && !relatedSegmentPointOnMid) {
                return;
            }

            const segmentsOnStart = getNetworkElementsByNodes([relatedSegmentPointOnStart]);
            const segmentsOnEnd = getNetworkElementsByNodes([relatedSegmentPointOnEnd]);
            const segmentsOnMid = getNetworkElementsByNodes([relatedSegmentPointOnMid]);

            const segmentOnStart = segmentsOnStart?.[0] as Line | undefined;
            const segmentOnEnd = segmentsOnEnd?.[0] as Line | undefined;
            const segmentOnMid = segmentsOnMid?.[0] as Line | undefined;

            // Determine which nodes are in the branch path (using O(1) lookup)
            const startInPath = relatedSegmentPointOnStart ? this.isInPath(relatedSegmentPointOnStart) : false;
            const endInPath = relatedSegmentPointOnEnd ? this.isInPath(relatedSegmentPointOnEnd) : false;
            
            // Helper function to assign size from segment
            const assignSizeFromSegment = (segment: Line | undefined, isRound: boolean): { width: number; height: number } | null => {
                if (!segment || !segment.net) {
                    return null;
                }
                
                if (isRound) {
                    const diameter = segment.net.diameterMil || segment.net.widthMil;
                    return { width: diameter, height: diameter };
                } else {
                    return { width: segment.net.widthMil, height: segment.net.heightMil };
                }
            };

            // Determine collector and pass segments based on order in branch path
            let collectorSegment: Line | undefined;
            let passSegment: Line | undefined;
            
            if (startInPath && endInPath) {
                // Both start and end are in path - determine order (using O(1) lookup)
                const startIndex = this.getPathIndex(relatedSegmentPointOnStart!);
                const endIndex = this.getPathIndex(relatedSegmentPointOnEnd!);
                
                if (startIndex < endIndex) {
                    // start is upstream (collector), end is downstream (pass)
                    collectorSegment = segmentOnStart;
                    passSegment = segmentOnEnd;
                } else {
                    // end is upstream (collector), start is downstream (pass)
                    collectorSegment = segmentOnEnd;
                    passSegment = segmentOnStart;
                }
            } else if (startInPath) {
                // Only start is in path - it's the collector
                collectorSegment = segmentOnStart;
                passSegment = segmentOnEnd; // pass might be on end if not in path
            } else if (endInPath) {
                // Only end is in path - it's the collector
                collectorSegment = segmentOnEnd;
                passSegment = segmentOnStart; // pass might be on start if not in path
            } else {
                // Neither in path - use start as collector, end as pass
                collectorSegment = segmentOnStart;
                passSegment = segmentOnEnd;
            }

            // Assign collector sizes
            if (collectorSegment) {
                const isRound = collectorSegment.segmentShape === ObjectTypes.round;
                const collectorSize = assignSizeFromSegment(collectorSegment, isRound);
                
                if (collectorSize) {
                    tee.widthCollectorMillimeter = collectorSize.width;
                    tee.heightCollectorMillimeter = collectorSize.height;
                    tee.shape = isRound ? ObjectTypes.round : ObjectTypes.rect;
                }
            }

            // Assign pass sizes
            if (passSegment) {
                const isRound = passSegment.segmentShape === ObjectTypes.round;
                const passSize = assignSizeFromSegment(passSegment, isRound);
                
                if (passSize) {
                    tee.widthPassMillimeter = passSize.width;
                    tee.heightPassMillimeter = passSize.height;
                    // Ensure shape is set if not already set from collector
                    if (!tee.shape) {
                        tee.shape = isRound ? ObjectTypes.round : ObjectTypes.rect;
                    }
                }
            }

            // Assign turn sizes (always from mid/side)
            if (segmentOnMid) {
                const isRound = segmentOnMid.segmentShape === ObjectTypes.round;
                const turnSize = assignSizeFromSegment(segmentOnMid, isRound);
                
                if (turnSize) {
                    tee.widthTurnMillimeter = turnSize.width;
                    tee.heightTurnMillimeter = turnSize.height;
                }
            }
        });
    }
    
    /**
     * Calculate and assign velocities to tees based on their sizes and collector flow rates
     */
    assignTeeVelocities(): void {
        const tees = this.elements.filter((elem) => elem instanceof Tee) as Tee[];
        
        tees.forEach((tee) => {
            if (!tee.net) return;
            
            // Calculate velocity based on tee shape and collector flow
            if (tee.shape === ObjectTypes.round) {
                // For round tees, use diameter (widthCollectorMillimeter is diameter)
                if (tee.widthCollectorMillimeter && tee.flowRateCollectorCmh) {
                    tee.net.velocity = getVelocityRound(tee.widthCollectorMillimeter, tee.flowRateCollectorCmh);
                }
            } else if (tee.shape === ObjectTypes.rect) {
                // For rectangular tees, use width and height
                if (tee.widthCollectorMillimeter && tee.heightCollectorMillimeter && tee.flowRateCollectorCmh) {
                    tee.net.velocity = getVelocityRect(tee.widthCollectorMillimeter, tee.heightCollectorMillimeter, tee.flowRateCollectorCmh);
                }
            }
        });
    }

    /**
     * Assign flow rates to tee crucial points based on connected segments
     * Finds segments connected to base1, base2 (main duct), and side (branch) crucial points
     * using graph to get line node IDs from fitting node IDs
     * and assigns their flow rates to tee.flowRateCollectorCmh and tee.flowRateTurnCmh
     */
    assignTeeFlowsOnCrucialPoints(): void {
        const graf = this.getGraph();
        if (!graf) return;
        
        (this.elements.filter(e => e instanceof Tee) as Tee[]).forEach(tee => {
            const crucialPoints = tee.getCrucialPoints();
            const start = crucialPoints.start;
            const end = crucialPoints.end;
            const mid = crucialPoints.mid;

            const relatedSegmentPointOnStart = graf.getLineNodeIdByFittingNodeId(start.id);
            const relatedSegmentPointOnEnd = graf.getLineNodeIdByFittingNodeId(end.id);
            const relatedSegmentPointOnMid = graf.getLineNodeIdByFittingNodeId(mid.id);

            if (!relatedSegmentPointOnEnd && !relatedSegmentPointOnStart && !relatedSegmentPointOnMid) {
                return;
            }

            const segmentsOnStart = getNetworkElementsByNodes([relatedSegmentPointOnStart]);
            const segmentsOnEnd = getNetworkElementsByNodes([relatedSegmentPointOnEnd]);
            const segmentsOnMid = getNetworkElementsByNodes([relatedSegmentPointOnMid]);

            const segmentOnStart = segmentsOnStart?.[0] as Line | undefined;
            const segmentOnEnd = segmentsOnEnd?.[0] as Line | undefined;
            const segmentOnMid = segmentsOnMid?.[0] as Line | undefined;

            const flowOnStart = segmentOnStart?.net?.flowRateCmh || 0;
            const flowOnEnd = segmentOnEnd?.net?.flowRateCmh || 0;
            const flowOnMid = segmentOnMid?.net?.flowRateCmh || 0;

            // Get flow direction type for this branch
            const flowType = tee.flowDirectionType.get(this.id) || 'UNKNOWN';
            
            // Determine which nodes are in the branch path (using O(1) lookup)
            const startInPath = relatedSegmentPointOnStart ? this.isInPath(relatedSegmentPointOnStart) : false;
            const endInPath = relatedSegmentPointOnEnd ? this.isInPath(relatedSegmentPointOnEnd) : false;
            const midInPath = relatedSegmentPointOnMid ? this.isInPath(relatedSegmentPointOnMid) : false;
            
            // Determine collector and pass based on order in branch path
            // Collector is upstream (earlier in path), Pass is downstream (later in path)
            let collectorFlow = 0;
            let turnFlow = flowOnMid; // Turn flow is always from mid (side)
            
            if (startInPath && endInPath) {
                // Both start and end are in path - determine order (using O(1) lookup)
                const startIndex = this.getPathIndex(relatedSegmentPointOnStart!);
                const endIndex = this.getPathIndex(relatedSegmentPointOnEnd!);
                
                if (startIndex < endIndex) {
                    // start is upstream (collector), end is downstream (pass)
                    collectorFlow = flowOnStart;
                } else {
                    // end is upstream (collector), start is downstream (pass)
                    collectorFlow = flowOnEnd;
                }
            } else if (startInPath) {
                // Only start is in path - it's the collector
                collectorFlow = flowOnStart;
            } else if (endInPath) {
                // Only end is in path - it's the collector
                collectorFlow = flowOnEnd;
            } else {
                // Neither in path - use larger flow as collector
                collectorFlow = Math.max(flowOnStart, flowOnEnd);
            }
            
            // Assign flows to tee
            tee.flowRateCollectorCmh = collectorFlow;
            tee.flowRateTurnCmh = turnFlow;
        })
    }
    
    /**
     * Calculate delta pressure for tees in this branch
     * Uses assigned flows and flowDirectionType with hydraulic resistance library to calculate dp
     * Assigns result to tee.net.dp
     */
    /**
     * Get resistance value from hydraulic resistance library based on tee parameters
     * @param tee - Tee element
     * @param flowType - Flow direction type ('on pass' or 'on turn')
     * @returns Resistance value or undefined if cannot be calculated
     */
    private getTeeResistance(tee: Tee, flowType: 'on pass' | 'on turn'): number | undefined {
        const isExhaust = tee.exhaustOrSupply === ObjectTypes.exhaust;
        const isRect = tee.shape === ObjectTypes.rect;
        const params = [
            tee.flowRateCollectorCmh,
            tee.flowRateTurnCmh,
            tee.widthCollectorMillimeter,
            tee.heightCollectorMillimeter,
            tee.widthTurnMillimeter,
            tee.heightTurnMillimeter,
            tee.widthPassMillimeter,
            tee.heightPassMillimeter,
            tee.angleDegree
        ];

        if (flowType === 'on pass') {
            if (isExhaust) {
                return isRect 
                    ? TeeExhaustRectangular.onPassResistance(...params)
                    : TeeExhaustRound.onPassResistance(...params);
            } else {
                return isRect
                    ? TeeSupplyRectangular.onPassResistance(...params)
                    : TeeSupplyRound.onPassResistance(...params);
            }
        } else {
            if (isExhaust) {
                return isRect
                    ? TeeExhaustRectangular.toTurnResistance(...params)
                    : TeeExhaustRound.toTurnResistance(...params);
            } else {
                return isRect
                    ? TeeSupplyRectangular.toTurnResistance(...params)
                    : TeeSupplyRound.toTurnResistance(...params);
            }
        }
    }

    /**
     * Calculate velocity for tee based on shape and collector flow
     * @param tee - Tee element
     * @returns Velocity in m/s or 0 if cannot be calculated
     */
    private calculateTeeVelocity(tee: Tee): number {
        if (!tee.flowRateCollectorCmh || !tee.widthCollectorMillimeter) {
            return 0;
        }

        if (tee.shape === ObjectTypes.round) {
            return getVelocityRound(tee.widthCollectorMillimeter, tee.flowRateCollectorCmh);
        } else if (tee.shape === ObjectTypes.rect && tee.heightCollectorMillimeter) {
            return getVelocityRect(tee.widthCollectorMillimeter, tee.heightCollectorMillimeter, tee.flowRateCollectorCmh);
        }

        return 0;
    }

    /**
     * Check if tee has all required data for dp calculation
     * @param tee - Tee element
     * @param flowType - Flow direction type
     * @returns true if tee can be used for dp calculation
     */
    private canCalculateTeeDp(tee: Tee, flowType: string | undefined): boolean {
        return !!(
            flowType && 
            flowType !== 'UNKNOWN' &&
            tee.exhaustOrSupply && 
            (tee.exhaustOrSupply === ObjectTypes.exhaust || tee.exhaustOrSupply === ObjectTypes.supply) &&
            tee.shape && 
            (tee.shape === ObjectTypes.rect || tee.shape === ObjectTypes.round) &&
            tee.flowRateCollectorCmh && 
            tee.flowRateCollectorCmh > 0 &&
            tee.net
        );
    }

    /**
     * Calculate delta pressure for tees in this branch
     * Uses assigned flows and flowDirectionType with hydraulic resistance library to calculate dp
     * Assigns result to tee.net.dp
     */
    calculateTeeDp(): void {
        const tees = this.elements.filter((elem) => elem instanceof Tee) as Tee[];
        
        tees.forEach((tee) => {
            const flowType = tee.flowDirectionType.get(this.id);
            
            if (!this.canCalculateTeeDp(tee, flowType)) {
                return;
            }

            const resistance = this.getTeeResistance(tee, flowType as 'on pass' | 'on turn');
            if (resistance === undefined || resistance === null) {
                return;
            }

            const velocity = this.calculateTeeVelocity(tee);
            if (velocity <= 0) {
                return;
            }

            const calculatedDp = getDpByKms(ObjectTypes.air, resistance, velocity);
            
            // Store DP and KMS per branch
            tee.branchKms.set(this.id, resistance);
            tee.branchDp.set(this.id, calculatedDp);
            
            // Also store in net for backward compatibility (use the last calculated value)
            tee.net.kms = resistance;
            tee.net.dp = calculatedDp;
        });
    }

    /**
     * Collect and sum delta pressure values from all elements in this branch
     * This method reads existing dp values without recalculating them
     * @returns The sum of all valid dp values from branch elements
     */
    collectDp(): number {
        // Sum up dp values, filtering out NaN, undefined, and null values
        const collectedDp = this.elements.reduce((sum, elem) => {
            const dpValue = elem.net?.dp;
            // Only add valid numbers (not NaN, undefined, or null)
            if (typeof dpValue === 'number' && !isNaN(dpValue) && isFinite(dpValue)) {
                return sum + dpValue;
            }
            return sum;
        }, 0);
        
        return collectedDp;
    }
}